import cleanDeep from 'clean-deep'
import { useNavigation } from 'hocs'
import { flowRight } from 'lodash'
import { QueryData } from 'navigation'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Hooks, PluginHook, Row, useExpanded, usePagination, useRowSelect, useSortBy } from 'react-table'
import { useSticky } from 'react-table-sticky'
import { Emitter } from 'utils/eventEmmiter'
import { pathUtils } from 'utils/pathnameFormatter'
import { Sorting } from '..'
import {
    CustomReactTableEvents,
    ExtensibleReactTableComponentProps,
    LoadDataParams,
    PaginationType,
    ReactTableInstance,
    ReactTableOptions,
    ReactTableRows,
    ReactTableState,
    ReactTableStateReducer,
    TableColumn,
} from './interface'
import { useCursorPagination } from './pagination/useCursorPagination'
import { t } from 'translations'

type SortDirection = 'asc' | 'desc'

export interface TableStateQueryParams {
    page: number
    pageSize: number
    dir?: SortDirection
    sortBy?: string
    cursor?: string
}

export class formatTableState {
    private static _formatPageSize(pageSize: number) {
        if ([10, 20, 25, 50, 100, 250, 500].includes(pageSize)) {
            return pageSize
        }

        return 10
    }

    static toQuery(query: QueryData): TableStateQueryParams {
        return {
            page: Number.parseInt(query.page, 10) || 1,
            pageSize: this._formatPageSize(Number.parseInt(query.pageSize, 10) || 10),
            dir: query.dir as SortDirection,
            sortBy: query.sortBy,
            cursor: query.cursor,
        }
    }

    static toTableInstance(query: QueryData): Partial<ReactTableState> {
        const pageIndex = Number.parseInt(query.page, 10) || 1
        const state = {} as Partial<ReactTableState>

        state.pageSize = this._formatPageSize(Number.parseInt(query.pageSize, 10) || 10)

        if (!query.cursor) {
            state.pageIndex = pageIndex - 1
        } else {
            state.token = query.cursor
        }

        if (query.sort) {
            state.sortBy = [{ id: query['sortBy'], desc: query['dir'] === 'desc' }]
        }

        return state
    }

    static toQueryFromTableState(tableState: Partial<ReactTableState>): TableStateQueryParams {
        const query = {
            page: (tableState.pageIndex || 0) + 1,
            pageSize: this._formatPageSize(tableState.pageSize || 10),
        } as TableStateQueryParams

        if (tableState.sortBy?.[0]) {
            query.sortBy = tableState.sortBy?.[0].id
            query.dir = tableState.sortBy?.[0].desc ? 'desc' : 'asc'
        }

        return query
    }

    static toQueryFromLoadData(
        params: Partial<LoadDataParams>,
        extraParams: Record<string, string | number | Array<string>> = {},
    ): string {
        if (!params) {
            return ''
        }

        const query = {
            page_size: params?.pagination?.pageSize,
            page: params?.pagination?.page,
            ...extraParams,
        } as any
        if (params?.sort?.prop) {
            query.sort_by = params.sort.prop
            query.sort_direction = params.sort.direction
        }

        return pathUtils.formatSearchParams(cleanDeep(query))
    }
}

interface UseHandleStateReducerProps<D extends object> {
    tableProps: ReactTableOptions<D>
    setTableState: (state: ReactTableState<D>) => void
    event?: Emitter<CustomReactTableEvents>
    stateReducer?: ReactTableStateReducer<D>
}

const useHandleStateReducer = <D extends object>({
    tableProps,
    setTableState,
    event,
    stateReducer,
}: UseHandleStateReducerProps<D>) => {
    const tableReducer = useMemo(() => {
        const innerStateReducer: ReactTableStateReducer<D> = (state, action, instance) => {
            setTableState(state)
            if (event) {
                switch (action.type) {
                    case 'toggleRowSelected':
                        event.emit('table_event_toggleRowSelected', {
                            action,
                            state,
                        })
                        break
                    case 'toggleAllRowsSelected':
                        event.emit('table_event_toggleAllRowsSelected', {
                            action,
                            state,
                        })
                        break
                    case 'resetSelectedRows':
                        event.emit('table_event_resetSelectedRows', {
                            action,
                            state,
                        })
                        break
                    default:
                        break
                }
            }

            return state
        }

        if (!!stateReducer) {
            return flowRight(innerStateReducer, stateReducer)
        } else {
            return innerStateReducer
        }
    }, [])

    tableProps.stateReducer = tableReducer
}

interface UseHandleSortingProps<D extends object> {
    tableProps: ReactTableOptions<D>
    plugins: PluginHook<D>[]
    sort?: Sorting | boolean
    loadData?: (tableState: LoadDataParams) => void
    storeStateInQuery: boolean
}

const useHandleSorting = <D extends object>({
    sort,
    loadData,
    tableProps,
    plugins,
    storeStateInQuery,
}: UseHandleSortingProps<D>) => {
    const navigation = useNavigation()
    const tableState = formatTableState.toQuery(navigation.query())

    if (sort) {
        if (!!loadData) {
            tableProps.manualSortBy = true
            tableProps.autoResetSortBy = false
        }
        if (!tableProps.initialState) {
            tableProps.initialState = {} as ReactTableState
        }

        if (typeof sort === 'object') {
            tableProps.initialState.sortBy = [{ id: sort.prop, desc: sort.direction === 'desc' }]
        }

        if (tableState.sortBy && storeStateInQuery) {
            tableProps.initialState.sortBy = [{ id: tableState.sortBy, desc: tableState.dir === 'desc' }]
        }

        plugins.push(useSortBy)
    }
}

interface UseHandleExpandedProps<D extends object> {
    expanded?: boolean | ((props: ExtensibleReactTableComponentProps<D>) => ReactNode)
    plugins: PluginHook<D>[]
    tableProps: ReactTableOptions<D>
}

const useHandleExpanded = <D extends object>({ expanded, plugins, tableProps }: UseHandleExpandedProps<D>) => {
    if (expanded) {
        plugins.push(useExpanded)
        tableProps.autoResetExpanded = false
    }
}

interface UseHandlePaginationProps<D extends object> {
    plugins: PluginHook<D>[]
    tableProps: ReactTableOptions<D>
    loadData?: (tableState: LoadDataParams) => void
    pagination?: PaginationType
    pageCount?: number
    storeStateInQuery?: boolean
}

const useHandlePagination = <D extends object>({
    pagination,
    tableProps,
    loadData,
    pageCount,
    plugins,
    storeStateInQuery,
}: UseHandlePaginationProps<D>) => {
    const navigation = useNavigation()
    const query = navigation.query()
    const tableStateFromQuery = formatTableState.toTableInstance(query)

    if (!pagination) {
        return
    }

    const isCursorPagination = typeof pagination !== 'boolean' && typeof pagination === 'object' && 'next' in pagination
    const isStandardPagination = typeof pagination !== 'object'
    tableProps.autoResetExpanded = true

    if (isStandardPagination) {
        if (!tableProps.initialState) {
            tableProps.initialState = {} as ReactTableState
        }

        if (!!loadData) {
            tableProps.manualPagination = true
            tableProps.autoResetPage = false
            tableProps.pageCount = pageCount
        } else {
            tableProps.manualPagination = false
        }

        if (!!tableStateFromQuery.pageIndex && storeStateInQuery) {
            tableProps.initialState.pageIndex = tableStateFromQuery.pageIndex
        }

        if (!!tableStateFromQuery.pageSize && storeStateInQuery) {
            tableProps.initialState.pageSize = tableStateFromQuery.pageSize
        }

        plugins.push(usePagination)

        return
    }

    if (isCursorPagination) {
        if (!tableProps.initialState) {
            tableProps.initialState = {} as ReactTableState
        }
        tableProps.autoResetExpanded = true
        tableProps.manualPagination = true

        if (!!tableStateFromQuery.pageSize && storeStateInQuery) {
            tableProps.initialState.pageSize = tableStateFromQuery.pageSize
        }

        plugins.push(useCursorPagination)
    }
}

interface UseHandleHiddenColumnsProps<D extends object> {
    tableProps: ReactTableOptions<D>
    storeStateInQuery: boolean
}

const useHandleHiddenColumns = <D extends object>({
    tableProps,
    storeStateInQuery,
}: UseHandleHiddenColumnsProps<D>) => {
    const navigation = useNavigation()
    const query = navigation.query()

    if (!!query.hiddenColumns?.length && storeStateInQuery) {
        if (!tableProps.initialState) {
            tableProps.initialState = {} as ReactTableState
        }

        tableProps.initialState.hiddenColumns = query.hiddenColumns.split('|')
    }
}

interface UseHandleRowSelectProps<D extends object = {}> {
    plugins: PluginHook<D>[]
    disableRowFromSelecting?: (row: Row<D>) => boolean
    rowSelect?: boolean
}

const useHandleRowSelect = <D extends object>({
    rowSelect,
    plugins,
    disableRowFromSelecting,
}: UseHandleRowSelectProps<D>) => {
    if (!!rowSelect) {
        plugins.push(useRowSelect)
        if (disableRowFromSelecting) {
            plugins.push((hooks: Hooks<D>) => {
                hooks.getToggleAllRowsSelectedProps = [
                    (props: any, { instance }: { instance: ReactTableInstance<D> }) => {
                        const isRowSelected = (row: ReactTableRows<D>) =>
                            row.isSelected || disableRowFromSelecting(row)
                        const isAllRowsSelected = instance.rows.every(isRowSelected)
                        const selectedRowsLength = Object.keys(instance.state.selectedRowIds).length

                        return [
                            props,
                            {
                                onChange: () => {
                                    instance.rows.forEach((row) => {
                                        if (!disableRowFromSelecting(row)) {
                                            // It uses Row properties instead of instance. Bug in TS.
                                            // @ts-ignore
                                            row.toggleRowSelected(!isAllRowsSelected)
                                        }
                                    })
                                },
                                style: { cursor: 'pointer' },
                                checked: isAllRowsSelected,
                                title: t('uiComponents.table.toggleAllRows'),
                                indeterminate: !isAllRowsSelected && selectedRowsLength,
                            },
                        ]
                    },
                ]
            })
        }
    }
}

interface UseHandleStickyProps<D extends object> {
    plugins: PluginHook<D>[]
    sticky?: boolean
}

const useHandleSticky = <D extends object>({ sticky, plugins }: UseHandleStickyProps<D>) => {
    if (!!sticky) {
        plugins.push(useSticky)
    }
}

interface UsePrepareTableInstanceOptionsProps<D extends object = {}> {
    data: D[]
    columns: TableColumn<D>[]
    pageCount?: number
    pagination?: PaginationType
    sort?: Sorting | boolean
    sticky?: boolean
    rowSelect?: boolean
    disableRowFromSelecting?: (row: Row<D>) => boolean
    expanded?: boolean | ((props: ExtensibleReactTableComponentProps<D>) => React.ReactNode)
    loadData?: (tableState: LoadDataParams) => void
    stateReducer?: ReactTableStateReducer<D>
    event?: Emitter<CustomReactTableEvents>
    storeStateInQuery: boolean
}

export const usePrepareTableInstanceOptions = <D extends object>({
    columns,
    data,
    sort,
    loadData,
    expanded,
    pagination,
    pageCount,
    rowSelect,
    disableRowFromSelecting,
    sticky,
    event,
    stateReducer,
    storeStateInQuery,
}: UsePrepareTableInstanceOptionsProps<D>) => {
    const [tableState, setTableState] = useState<ReactTableState<D>>()
    const plugins = [] as PluginHook<D>[]
    const tableProps = {
        columns: columns as unknown as ReactTableOptions<D>['columns'],
        data,
    } as ReactTableOptions<D>

    useHandleStateReducer({ event, setTableState, tableProps, stateReducer })
    useHandleSorting({ sort, loadData, tableProps, plugins, storeStateInQuery })
    useHandleExpanded({ expanded, plugins, tableProps })
    useHandlePagination({ storeStateInQuery, pagination, tableProps, loadData, pageCount, plugins })
    useHandleHiddenColumns({ tableProps, storeStateInQuery })
    useHandleRowSelect({ rowSelect, disableRowFromSelecting, plugins })
    useHandleSticky({ sticky, plugins })

    return {
        tableProps,
        plugins,
        immutableTableState: tableState,
    }
}

interface UseHandleUserEventsProps<D extends object> {
    event?: Emitter<CustomReactTableEvents>
    tableInstance: ReactTableInstance<D>
    storeStateInQuery: boolean
}

export const useHandleUserEvents = <D extends object>({
    event,
    tableInstance,
    storeStateInQuery,
}: UseHandleUserEventsProps<D>) => {
    const navigation = useNavigation()

    useEffect(() => {
        if (!!event) {
            event.on('toggleHideColumn', (columnId) => {
                if (storeStateInQuery) {
                    const hiddenColumns = [...(tableInstance.state.hiddenColumns ?? [])]
                    if (tableInstance.state.hiddenColumns?.includes(columnId)) {
                        navigation.addQuery({
                            hiddenColumns: hiddenColumns.filter((id) => id !== columnId)?.join('|'),
                        })
                    } else {
                        hiddenColumns.push(columnId)
                        navigation.addQuery({
                            hiddenColumns: hiddenColumns.join('|'),
                        })
                    }
                }
                tableInstance.toggleHideColumn(columnId)
            })

            event.on('setHiddenColumns', (columnIds) => {
                tableInstance.setHiddenColumns(columnIds)
            })

            event.on('toggleHideAllColumns', () => {
                tableInstance.toggleHideAllColumns(true)
            })

            event.on('toggleRowSelected', (rowId) => {
                tableInstance.toggleRowSelected(rowId)
            })

            event.on('toggleAllRowsSelected', (selected) => {
                tableInstance.toggleAllRowsSelected(selected)
            })

            event.on('resetSelectedRows', () => {
                tableInstance.toggleAllRowsSelected(false)
            })

            event.on('resetToken', () => {
                tableInstance.setPaginationToken && tableInstance.setPaginationToken('0')
            })
        }
    }, [])
}

interface UseHandleDataLoadingProps<D extends object> {
    loadData?: (tableState: LoadDataParams) => void
    tableInstance: ReactTableInstance<D>
}

export const useHandleDataLoading = <D extends object>({
    tableInstance,
    loadData,
}: UseHandleDataLoadingProps<D>) => {
    const { pageIndex, pageSize, sortBy, token } = tableInstance.state as any

    useEffect(() => {
        if (!!loadData) {
            loadData({
                pagination: {
                    page: pageIndex + 1,
                    pageSize: pageSize,
                },
                sort: {
                    direction: sortBy?.[0]?.desc ? 'desc' : 'asc',
                    prop: sortBy?.[0]?.id,
                },
                token,
            } as LoadDataParams)
        }
    }, [loadData, pageIndex, pageSize, sortBy, token])
}

interface UseUpdateTableStateInQueryProps<D extends object> {
    tableInstance: ReactTableInstance<D>
    storeStateInQuery: boolean
}

const useUpdateTableStateInQuery = <D extends object>({
    tableInstance,
    storeStateInQuery,
}: UseUpdateTableStateInQueryProps<D>) => {
    const { pageIndex, pageSize, sortBy, token } = tableInstance.state as any
    const navigation = useNavigation()

    useEffect(() => {
        if (storeStateInQuery) {
            const queryStr = formatTableState.toQueryFromTableState({
                pageIndex,
                pageSize,
                sortBy,
            })

            navigation.addQueryWithReplace(queryStr as unknown as any)
        }
    }, [pageIndex, pageSize, sortBy, token, storeStateInQuery])
}

interface UsePostInitiTableInstanceProps<D extends object> {
    tableInstance: ReactTableInstance<D>
    event?: Emitter<CustomReactTableEvents>
    loadData?: (tableState: LoadDataParams) => void
    storeStateInQuery: boolean
}

export const usePostInitiTableInstance = <D extends object>({
    tableInstance,
    event,
    loadData,
    storeStateInQuery,
}: UsePostInitiTableInstanceProps<D>) => {
    useHandleUserEvents({ tableInstance, event, storeStateInQuery })
    useHandleDataLoading({ tableInstance, loadData })
    useUpdateTableStateInQuery({ tableInstance, storeStateInQuery })
}
